'use client'

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'

// Типы режимов просмотра
export type ViewMode = 'user' | 'admin'

// Интерфейс контекста режима просмотра
interface ViewModeContextType {
  viewMode: ViewMode
  setViewMode: (mode: ViewMode) => void
  isAdminMode: boolean
  isUserMode: boolean
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined)

// Ключ для хранения в localStorage
const STORAGE_KEY = 'podkova-view-mode'

export function ViewModeProvider({ children }: { children: ReactNode }) {
  const [viewMode, setViewModeState] = useState<ViewMode>('user')
  const [isHydrated, setIsHydrated] = useState(false)

  // Загружаем состояние из localStorage при монтировании
  useEffect(() => {
    setIsHydrated(true)
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'admin') {
      setViewModeState('admin')
    }
  }, [])

  // Сохраняем состояние в localStorage при изменении
  useEffect(() => {
    if (isHydrated) {
      localStorage.setItem(STORAGE_KEY, viewMode)
    }
  }, [viewMode, isHydrated])

  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode)
  }, [])

  return (
    <ViewModeContext.Provider
      value={{
        viewMode,
        setViewMode,
        isAdminMode: viewMode === 'admin',
        isUserMode: viewMode === 'user',
      }}
    >
      {children}
    </ViewModeContext.Provider>
  )
}

// Хук для использования контекста режима просмотра
export function useViewMode() {
  const context = useContext(ViewModeContext)
  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider')
  }
  return context
}
